import React, { useEffect, useState } from "react";
import { Box, Chip, Stack, Switch, Typography, Avatar } from "@mui/material";
import MUIDataTable, { debounceSearchRender } from "mui-datatables";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import axios from "axios";
import Loader from "../../../components/loader/Loader";
import {
  hideConfirmModal,
  showConfirmModal,
} from "../../../redux/counterSlice";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { useTableHeight } from "../../../hooks/useTablePagination";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { useSelector } from "react-redux";

function FastDelivery() {
  const dispatch = useDispatch();
  const tableHeight = useTableHeight();

  const [loader, setLoader] = useState(true);
  const [formData, setFormData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [activeCount, setActiveCount] = useState(0);
  const [fastCount, setFastCount] = useState(0);
  const weightList = useSelector((state) => state.counter.weightList);

  const loadData = async (
    page = 1,
    perPage = pageSize,
    search = searchText,
  ) => {
    try {
      setLoader(true);
      const url = search?.trim() ? "fastestDelivery/search" : "fastestDelivery";
      const params = { paginate: true, per_page: perPage, page };
      if (search?.trim()) params.q = search.trim();
      const response = await axios.get(url, { params });
      const result = response?.data?.data;

      if (Array.isArray(result)) {
        // API returned everything, unpaginated — slice it ourselves
        const sorted = [...result].sort((a, b) => b.id - a.id);
        const start = (page - 1) * perPage;
        const pageSlice = sorted.slice(start, start + perPage);
        setFormData(pageSlice);
        setTotalCount(response?.data?.total ?? result.length);
      } else {
        const sorted = [...(result?.data || [])].sort((a, b) => b.id - a.id);
        setFormData(sorted);
        setTotalCount(result?.total || 0);
      }
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoader(false);
    }
  };
  useEffect(() => {
    loadData(pageNo + 1, pageSize, searchText);
  }, [pageNo, pageSize]);
  useEffect(() => {
    loadAllCounts();
  }, []);
  const toggleFastDelivery = (id) => {
    setLoader(true);
    axios
      .get("/fastDeliverySwitch/" + id)
      .then((response) => {
        toast.success(response?.data?.message || "Fast delivery updated!");
        loadData(pageNo + 1, pageSize, searchText);
        loadAllCounts();
      })
      .catch((err) => toast.error(err?.message))
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal({ msg: "" }));
      });
  };
  const loadAllCounts = async () => {
    try {
      let page = 1;
      let totalPages = 1;

      let active = 0;
      let fast = 0;

      do {
        const res = await axios.get("fastestDelivery", {
          params: { paginate: true, per_page: 100, page },
        });

        const paginated = res?.data?.data || {};
        const data = paginated.data || [];

        // ✅ accumulate counts
        active += data.filter((i) => i.active_status === 1).length;
        fast += data.filter((i) => i.fast_delivery === 1).length;

        totalPages = paginated.last_page || 1;
        page++;
      } while (page <= totalPages);

      setActiveCount(active);
      setFastCount(fast);
    } catch (err) {
      console.error(err);
    }
  };
  const columns = [
    {
      name: "id",
      label: "S.No",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => (
          <Typography variant="body2" color="text.secondary">
            {pageNo * pageSize + dataIndex + 1}
          </Typography>
        ),
      },
    },
    {
      name: "images",
      label: "Image",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const imageUrl = formData[dataIndex]?.images?.[0]?.app_product_image;
          return imageUrl ? (
            <PhotoProvider>
              <PhotoView src={imageUrl}>
                <img
                  src={imageUrl}
                  alt=""
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 10,
                    cursor: "pointer",
                    objectFit: "cover",
                    border: "2px solid #f0f0f0",
                  }}
                />
              </PhotoView>
            </PhotoProvider>
          ) : (
            <Avatar
              variant="rounded"
              sx={{ width: 52, height: 52, bgcolor: "action.hover" }}
            >
              <StorefrontIcon sx={{ color: "#ccc" }} />
            </Avatar>
          );
        },
      },
    },
    {
      name: "product_name",
      label: "Product Name",
      options: {
        filter: false,
        customBodyRender: (value) => (
          <Typography
            variant="body2"
            fontWeight={600}
            title={value}
            sx={{
              maxWidth: 200,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {value}
          </Typography>
        ),
      },
    },
    {
      name: "category",
      label: "Category",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => (
          <Typography variant="body2" color="text.secondary">
            {formData[dataIndex]?.category?.category_name ?? "-"}
          </Typography>
        ),
      },
    },
    {
      name: "sub_category",
      label: "Sub Category",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => (
          <Typography variant="body2" color="text.secondary">
            {formData[dataIndex]?.sub_category?.sub_category_name ?? "-"}
          </Typography>
        ),
      },
    },
    {
      name: "variation",
      label: "Egg Price",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const variations = formData[dataIndex]?.variation || [];
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {variations.map((v) => {
                const weightObj = weightList.find(
                  (w) => w.weight_id === v.weight_id,
                );
                const weightName = weightObj
                  ? `${weightObj.weight_name} ${weightObj.unit?.unit_name}`
                  : v.weight_id;
                return (
                  <Box
                    key={v.variation_id}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                      borderLeft: "3px solid #e65100",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Variant: <strong>{weightName}</strong>
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: Number(v.egg) > 0 ? "#e65100" : "#999",
                        fontWeight: 600,
                      }}
                    >
                      Price: {Number(v.egg) > 0 ? `₹${v.egg}` : "-"}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        },
      },
    },
    {
      name: "variation",
      label: "Eggless Price",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const variations = formData[dataIndex]?.variation || [];
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {variations.map((v) => {
                const weightObj = weightList.find(
                  (w) => w.weight_id === v.weight_id,
                );
                const weightName = weightObj
                  ? `${weightObj.weight_name} ${weightObj.unit?.unit_name}`
                  : v.weight_id;
                return (
                  <Box
                    key={v.variation_id}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                      borderLeft: "3px solid #2e7d32",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Variant: <strong>{weightName}</strong>
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: Number(v.eggLess) > 0 ? "#2e7d32" : "#999",
                        fontWeight: 600,
                      }}
                    >
                      Price: {Number(v.eggLess) > 0 ? `₹${v.eggLess}` : "-"}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        },
      },
    },
    {
      name: "active_status",
      label: "Active Status",
      options: {
        filter: true,
        filterOptions: {
          names: ["Active", "Inactive"],
          logic(value, filters) {
            if (filters.length) {
              if (filters.includes("Active") && +value === 1) return false;
              if (filters.includes("Inactive") && +value === 0) return false;
              return true;
            }
            return false;
          },
        },
        customBodyRenderLite: (dataIndex) => {
          const status = formData[dataIndex]?.active_status;
          return (
            <Chip
              icon={
                status === 1 ? (
                  <CheckCircleOutlineIcon fontSize="small" />
                ) : (
                  <CancelOutlinedIcon fontSize="small" />
                )
              }
              label={status === 1 ? "Active" : "Inactive"}
              size="small"
              color={status === 1 ? "success" : "error"}
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: "11px" }}
            />
          );
        },
      },
    },
    {
      name: "fast_delivery",
      label: "Fast Delivery",
      options: {
        filter: true,
        setCellHeaderProps: () => ({ align: "center" }),
        setCellProps: () => ({ align: "center" }),
        filterOptions: {
          names: ["Fast Delivery", "Normal Delivery"],
          logic(value, filters) {
            if (filters.length) {
              if (filters.includes("Fast Delivery") && +value === 1)
                return false;
              if (filters.includes("Normal Delivery") && +value === 0)
                return false;
              return true;
            }
            return false;
          },
        },
        customBodyRenderLite: (dataIndex) => {
          const item = formData[dataIndex];
          const status = item?.fast_delivery;

          // Get price from first variant
          const variant = item?.variation?.[0];
          const price = variant?.selling_price ?? variant?.price ?? null;

          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Switch
                  size="small"
                  checked={status === 1}
                  onChange={() =>
                    dispatch(
                      showConfirmModal({
                        msg:
                          status === 1
                            ? "Disable Fast Delivery"
                            : "Enable Fast Delivery",
                        action: () => toggleFastDelivery(item?.id),
                      }),
                    )
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#ff9800" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#ff9800",
                    },
                  }}
                />
                {status === 1 && (
                  <BoltIcon sx={{ color: "#ff9800", fontSize: 18 }} />
                )}
              </Box>
              {price !== null && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  ₹{price}
                </Typography>
              )}
            </Box>
          );
        },
      },
    },
  ];

  const options = {
    fixedHeader: true,
    tableBodyHeight: tableHeight,
    selectableRows: false,
    download: false,
    viewColumns: true,
    print: false,
    serverSide: true,
    filter: true,
    searchText,
    customSearchRender: debounceSearchRender(500),
    onSearchChange: (val) => {
      setSearchText(val || "");
      setPageNo(0);
      loadData(1, pageSize, val || "");
    },
    onSearchClose: () => {
      setSearchText("");
      setPageNo(0);
      loadData(1, pageSize, "");
    },
    rowsPerPage: pageSize,
    page: pageNo,
    count: totalCount,
    onChangeRowsPerPage: (newSize) => {
      setPageSize(newSize);
      setPageNo(0);
      loadData(1, newSize, searchText);
    },
    onChangePage: (currentPage) => {
      setPageNo(currentPage);
    },
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* <BoltIcon sx={{ color: "#ff9800", fontSize: 28 }} /> */}
              <Typography variant="h6" fontWeight={700} color="text.primary">
                Fast Delivery Products
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Chip
                label={`Total: ${totalCount}`}
                color="primary"
                size="small"
              />
              <Chip
                label={`Active: ${activeCount}`}
                color="success"
                size="small"
              />

              <Chip
                icon={<BoltIcon fontSize="small" />}
                label={`Fast: ${fastCount}`}
                size="small"
                sx={(theme) => ({
                  bgcolor: theme.palette.mode === "dark" ? "#3d2a00" : "#fff3e0",
                  color: "#e65100",
                  fontWeight: 700,
                  "& .MuiChip-icon": { color: "#ff9800" },
                })}
              />
            </Stack>
          </Box>

          <MUIDataTable
            data={formData}
            columns={columns}
            options={options}
            title=""
          />
        </>
      )}
    </>
  );
}

export default FastDelivery;
